import { useCallback } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import jsPDF from 'jspdf';

interface ExportMessage {
  role: string;
  content: string;
  created_at?: string;
}

interface ExportOptions {
  title: string;
  model: string;
  messages: ExportMessage[];
  createdAt?: string;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
}

function downloadFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return format(new Date(), "d 'de' MMMM 'de' yyyy, HH:mm", { locale: ptBR });
  return format(new Date(dateStr), "d 'de' MMMM 'de' yyyy, HH:mm", { locale: ptBR });
}

function generateHTML({ title, model, messages, createdAt }: ExportOptions): string {
  const date = formatDate(createdAt);
  const messagesHtml = messages
    .map((msg) => {
      const isUser = msg.role === 'user';
      const bgColor = isUser ? '#dbeafe' : '#f3f4f6';
      const label = isUser ? '👤 Você' : '🤖 Assistente';
      const time = msg.created_at ? format(new Date(msg.created_at), 'HH:mm', { locale: ptBR }) : '';
      const escapedContent = msg.content
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br/>');

      return `
        <div style="margin-bottom:16px;padding:12px 16px;border-radius:12px;background:${bgColor};max-width:80%;">
          <div style="font-size:12px;color:#6b7280;margin-bottom:4px;">${label}${time ? ` · ${time}` : ''}</div>
          <div style="font-size:14px;line-height:1.6;color:#1f2937;white-space:pre-wrap;">${escapedContent}</div>
        </div>`;
    })
    .join('\n');

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1.0">
  <title>${title}</title>
  <style>
    body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:800px;margin:0 auto;padding:32px 24px;background:#fff;color:#1f2937;}
    .header{border-bottom:1px solid #e5e7eb;padding-bottom:16px;margin-bottom:24px;}
    .header h1{font-size:24px;margin:0 0 8px;}
    .header .meta{font-size:13px;color:#6b7280;}
  </style>
</head>
<body>
  <div class="header">
    <h1>💬 ${title}</h1>
    <div class="meta">Exportado em ${date} · Modelo: ${model}</div>
  </div>
  ${messagesHtml}
</body>
</html>`;
}

function generatePDF({ title, model, messages, createdAt }: ExportOptions) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const maxWidth = pageWidth - margin * 2;
  const date = formatDate(createdAt);

  function addHeader() {
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    doc.text(title, margin, 12);
    doc.text(date, pageWidth - margin, 12, { align: 'right' });
    doc.setDrawColor(229, 231, 235);
    doc.line(margin, 15, pageWidth - margin, 15);
  }

  let y = 25;
  addHeader();

  for (const msg of messages) {
    const isUser = msg.role === 'user';
    const label = isUser ? 'Você:' : 'Assistente:';

    // Label
    doc.setFont('Helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(isUser ? 37 : 75, isUser ? 99 : 85, isUser ? 235 : 99);

    if (y > pageHeight - 30) {
      doc.addPage();
      y = 25;
      addHeader();
    }

    doc.text(label, margin, y);
    y += 6;

    // Content
    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(31, 41, 55);
    const lines = doc.splitTextToSize(msg.content, maxWidth);

    for (const line of lines) {
      if (y > pageHeight - 20) {
        doc.addPage();
        y = 25;
        addHeader();
      }
      doc.text(line, margin, y);
      y += 5;
    }

    y += 6;
  }

  doc.save(`${slugify(title) || 'conversa'}.pdf`);
}

export function useExportConversation() {
  const exportAsHTML = useCallback((options: ExportOptions) => {
    const html = generateHTML(options);
    const filename = `${slugify(options.title) || 'conversa'}.html`;
    downloadFile(html, filename, 'text/html;charset=utf-8');
  }, []);

  const exportAsPDF = useCallback((options: ExportOptions) => {
    generatePDF(options);
  }, []);

  return { exportAsHTML, exportAsPDF };
}
